import { BaseModal } from './BaseModal'
import {
  GUESS_DISTRIBUTION_TEXT,
} from '../../constants/strings'

import { StatBar } from '../stats/StatBar'
import { Histogram } from '../stats/Histogram'
import { GameStats, getStoredDeviceId } from '../../lib/localStorage'
import { setStatsLocalAndRemote, DEFAULT_STATS } from '../../lib/stats'
import { User } from '../../lib/user'
import { mergeStats, loadLocalStats } from '../../lib/stats'

type Props = {
  merge: boolean // whether to merge or import
  isOpen: boolean
  handleClose: () => void
  user: User,
  onUpdateStats: (stats: GameStats) => void
}

type ModalHandler = {
  title: string
  description: JSX.Element,
  prompt: string
  yes: () => void
  no: () => void
  yesLabel: string
  noLabel: string
}

export const ImportStatsModal = ({
  merge,
  isOpen,
  handleClose,
  user,
  onUpdateStats,
}: Props) => {

  const localStats = loadLocalStats()
  const addSyncedDeviceId = (gameStats: GameStats) => {
    if(!gameStats.syncedDeviceIds?.length) {
      gameStats.syncedDeviceIds = [];
    }
    gameStats.syncedDeviceIds.push(getStoredDeviceId());
    gameStats.syncedDeviceIds = [...new Set<string>(gameStats.syncedDeviceIds)];
    return gameStats
  }

	const importStats = async () => {
		if(localStats && window.confirm(
        'Are you sure you want to override the statistics for this account? This action is not reversable.'
      )) {
      let newStats = addSyncedDeviceId(localStats);
      if(merge) {
        newStats = mergeStats(localStats, user.stats())
      }
			await setStatsLocalAndRemote(user, newStats);
      onUpdateStats(newStats);
		}
		handleClose()
	}

  const welcomeBackHandler: ModalHandler = {
    title: 'Welcome back!',
    description: <>Looks you have some stats on this device not associated with this account</>,
    prompt: 'Use these local stats?',
    yes: importStats,
    no: async () => {
      // TODO make sure this is also checking firebase stats
      const gameStats = addSyncedDeviceId(user.stats());
      await setStatsLocalAndRemote(user, gameStats)
      onUpdateStats(gameStats)
		  handleClose()
	  },
    yesLabel: 'Yes, use my local stats',
    noLabel: 'No, use my existing stats for this account',
  }
  
  
  const importHandler: ModalHandler = {
    title: 'Welcome to Polygonle+',
    description: <>Let's get your account set up!<br /><br /> Looks like you have existing stats.</>,
    prompt: 'Do you want to import these into your new account?',
    yes: importStats,
    no: async () => {
      if(window.confirm(
        'Are you sure you want to clear your statistics for this account? This action is not reversable.'
      )) {
        const gameStats = addSyncedDeviceId(DEFAULT_STATS);
        await setStatsLocalAndRemote(user, gameStats)
        onUpdateStats(DEFAULT_STATS)
      }
		  handleClose()
	  },
    yesLabel: 'Yes, import stats',
    noLabel: 'No, I want to start over',
  }

  const mergeHandler: ModalHandler = {
    title: 'Using a new device?',
    description: <>We found some unsynced stats on this device.</>,
    prompt: 'Do you want to merge these into your account?',
    yes: importStats,
    no: async () => {
      // get current stats for the user?
      const gameStats = addSyncedDeviceId(user.stats());
      await setStatsLocalAndRemote(user, gameStats)
		  handleClose()
	  },
    yesLabel: 'Yes, merge stats',
    noLabel: 'No, keep my existing stats',
  }

  const hasStats = localStats && localStats.totalGames > 0;
  const hasAccountStats = user.stats().totalGames > 0;
  const handler = merge ? mergeHandler : (
    hasAccountStats ? welcomeBackHandler : importHandler
  );

	const statsImportPrompt = (gameStats: GameStats) => {
		return (<div className="p-2">
			<p className="text-gray-700 dark:text-gray-300">{handler.description}</p>
			<div className="my-4 border dark:border-gray-700 border-gray-300 rounded-md">
      	<StatBar gameStats={user.stats()} mergeStats={gameStats} />
      	<h4 className="text-lg leading-6 font-medium text-gray-900 dark:text-gray-100">
      	  {GUESS_DISTRIBUTION_TEXT}
      	</h4>
      {merge && 
      	<Histogram
      	  gameStats={user.stats()}
      	  isGameWon={false}
      	  numberOfGuessesMade={0}
          mergeStats={gameStats}
      	/>
      }
      {!merge && 
      	<Histogram
      	  gameStats={gameStats}
      	  isGameWon={false}
      	  numberOfGuessesMade={0}
      	/>
      }
			</div>
			<p className="text-gray-700 dark:text-gray-300">{handler.prompt}</p>
			<div className="p-2 flex flex-row">
				<button
					onClick={handler.yes}
      	  id="import-stats"
      	  type="button"
      	  className="inline-flex justify-center items-center text-center w-full rounded-md border border-transparent shadow-sm px-4 py-4 bg-indigo-600 text-base font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:text-sm m-2">
          {handler.yesLabel}
      	</button>
				<button
					onClick={handler.no}
      	  id="import-stats"
      	  type="button"
      	  className="inline-flex justify-center items-center text-center w-full rounded-md border border-transparent shadow-sm px-4 py-4 bg-gray-600 text-base font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:text-sm m-2">
          {handler.noLabel}
      	</button>
			</div>
		</div>)
	}
  return (
    <BaseModal title={handler.title} isOpen={isOpen} handleClose={handleClose}>
			{hasStats && statsImportPrompt(localStats)}
 		</BaseModal>
		)
}
